/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Managing contracts", lastUpdatedDate = new Date(1713312000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Managing contracts",
        description: null,
        lastUpdatedDate: "Wed Apr 17 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "depth": 1,
          "id": "event-definition"
        }, {
          "depth": 1,
          "id": "event-versioning"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      ul: "ul",
      li: "li",
      strong: "strong",
      h3: "h3",
      a: "a",
      img: "img",
      pre: "pre",
      code: "code",
      span: "span"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "Hightouch Contracts helps marketing and data teams plan, manage, and enforce the\ndata they track through event collection."
      }), "\n", _jsx(_components.p, {
        children: "Contracts have two components::"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["A ", _jsx(_components.strong, {
            children: "contract event"
          }), " defines the name, type, description, and validation\nschema of a single event (for example, Order Completed)"]
        }), "\n", _jsxs(_components.li, {
          children: ["The ", _jsx(_components.strong, {
            children: "contract"
          }), " is a set of contract events that can be applied to an event\nsource (for example, E-commerce Events)"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Each contract event can only belong to a single contract, but a contract can be\napplied to multiple event sources."
      }), "\n", _jsx(_components.h3, {
        id: "event-definition",
        children: _jsx(_components.strong, {
          children: "Event definition"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The most important part of creating a contract event is defining the validation\nschema. Hightouch uses ", _jsx(_components.a, {
          href: "https://json-schema.org/",
          children: "JSON Schema"
        }), " to define rules\nfor validating the event payload."]
      }), "\n", _jsx(_components.p, {
        children: "When creating an event, you can either use our visual editor to define a\nJSON Schema, or paste a custom schema definition into Hightouch."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "events/contracts/create-event.png",
          alt: "Event creation screenshot"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch uses\n", _jsx(_components.a, {
          href: "https://json-schema.org/draft-07/json-schema-release-notes",
          children: "JSON Schema Draft 7"
        }), ".\nThis allows for highly complex validation of event payloads, including\n", _jsx(_components.a, {
          href: "https://json-schema.org/understanding-json-schema/reference/regular_expressions",
          children: "validating field values with regular expressions"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "event-versioning",
        children: _jsx(_components.strong, {
          children: "Event versioning"
        })
      }), "\n", _jsx(_components.p, {
        children: "Hightouch allows you to manage different versions of an contract event at the\nsame time. This is often useful when it is difficult to migrate your codebase to\nuse the latest payload. For example, when an iOS app takes a while to be updated\nacross all devices."
      }), "\n", _jsx(_components.p, {
        children: "After creating your first event, you can create additional versions of the\nschema. The first version is the \"default\" version, which applies to events that\ndo not specify a version in the payload. It is recommended that your events\nfollow an incrementing pattern (for example, v1, v2, v3), although not strictly\nrequired."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "events/contracts/editing-versions.png",
          alt: "Event versioning screenshot"
        })
      }), "\n", _jsx(_components.p, {
        children: "When invoking an event, you can specify the schema version via:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["client", _jsx(_components.span, {
            className: "hljs-selector-class",
            children: ".track"
          }), "('", _jsx(_components.span, {
            className: "hljs-attribute",
            children: "Order"
          }), " Completed', {\n  ...\n}, {\n  context: {\n    htevents: {\n      schemaVersion: ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"v2\""
          }), "\n    }\n  }\n});\n"]
        })
      })]
    });
  }
}
export default MDXContent;
